/* AcademiaTeaserBanner — bottom-of-page red+gold attention banner for the
   Academia VIP. Visible to everyone (anon + logged-in) per owner spec.
   - Anon → "Entra subito" navigates to the landing /#vip-hero anchor.
   - Logged-in → "Entra subito" navigates to /vip (academy catalog).
*/
import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Crown, Sparkles, ArrowRight } from "lucide-react";

export default function AcademiaTeaserBanner({ ctaHref = "/vip", testId = "academia-teaser" }) {
  return (
    <section className="relative px-4 sm:px-6 py-10" data-testid={testId}>
      <div className="max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.55, ease: "easeOut" }}
          className="relative overflow-hidden rounded-3xl"
          style={{
            background:
              "linear-gradient(135deg, #7A1322 0%, #B91C1C 25%, #E0A82E 50%, #DC2626 75%, #7A1322 100%)",
            backgroundSize: "300% 300%",
            animation: "vip-shimmer 6s ease-in-out infinite",
            boxShadow:
              "0 24px 70px -28px rgba(220, 38, 38, 0.65), 0 12px 36px -18px rgba(184, 134, 47, 0.5)",
            border: "1px solid rgba(255, 220, 130, 0.45)",
          }}
        >
          {/* Sweep highlight */}
          <span
            aria-hidden="true"
            className="pointer-events-none absolute -inset-1"
            style={{
              background:
                "linear-gradient(110deg, transparent 30%, rgba(255,255,255,0.18) 50%, transparent 70%)",
              backgroundSize: "200% 100%",
              animation: "vip-sweep 4s linear infinite",
              mixBlendMode: "overlay",
            }}
          />
          {/* Grain overlay */}
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 opacity-[0.06]"
            style={{
              backgroundImage:
                "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2'/><feColorMatrix values='0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 0.85 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
            }}
          />

          <div className="relative px-6 sm:px-10 py-10 sm:py-12 flex flex-col sm:flex-row items-start sm:items-center gap-6 text-white">
            {/* NEW badge */}
            <div className="flex flex-col gap-3 flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] uppercase tracking-[0.22em] font-bold"
                  style={{
                    background: "linear-gradient(135deg, #FFFBEE, #FFE9B0)",
                    color: "#7A1322",
                    boxShadow: "0 4px 12px -4px rgba(0,0,0,0.35)",
                  }}
                >
                  <Sparkles className="h-3 w-3" /> NUOVO
                </span>
                <span className="text-[11px] uppercase tracking-[0.22em] opacity-90">
                  · Esclusivo
                </span>
              </div>
              <h2
                className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight"
                style={{ textShadow: "0 2px 8px rgba(0,0,0,0.35)" }}
              >
                Academia VIP — Médicos para Italia
              </h2>
              <p className="max-w-2xl text-sm sm:text-base"
                 style={{ color: "rgba(255,248,231,0.92)" }}>
                Tutto il programma ministeriale, in un'unica academy. 6 moduli ·
                37 lezioni · oltre 32 ore audio · atlante illustrato · simulatore
                esame reale. La prima lezione di ogni modulo è gratuita.
              </p>
            </div>

            {/* CTA — large pill button */}
            <Link
              to={ctaHref}
              data-testid={`${testId}-cta`}
              className="group relative inline-flex items-center gap-2 px-7 py-4 rounded-2xl font-semibold transition-all hover:scale-[1.04] active:scale-[0.98] whitespace-nowrap shrink-0"
              style={{
                background: "linear-gradient(135deg, #FFFBEE 0%, #FFE9B0 100%)",
                color: "#7A1322",
                boxShadow:
                  "0 10px 28px -10px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.6)",
                border: "1px solid rgba(184, 134, 47, 0.45)",
              }}
            >
              <Crown
                className="h-5 w-5 transition-transform group-hover:rotate-[-6deg]"
                style={{ color: "#B8862F" }}
              />
              <span className="font-serif text-lg">Entra subito</span>
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
